import { registerComponent } from "helpers";
import { WYATTElement } from "wyatt";

registerComponent(
  "wyatt-tabs",
  class WyattTabs extends WYATTElement {
    constructor() {
      super({
        triggersSelector: ".tabs__triggers",
        triggerSelector: ".tabs__trigger",
        panelSelector: ".tabs__panel",
        activeTab: 0,
      });

      this.triggers = Array.from(
        this.querySelectorAll(this.config.triggerSelector),
      );
      this.panels = Array.from(
        this.querySelectorAll(this.config.panelSelector),
      );

      // Guard: <wyatt-tabs> rendered without trigger/panel children would
      // null-deref in addAccessibilityAttributes() and activatePanel(). Bail
      // out so the component is a safe no-op instead of throwing on init.
      if (!this.triggers.length || !this.panels.length) {
        return;
      }

      this.addAccessibilityAttributes();

      this.activatePanel(this.config.activeTab, false);

      this.triggers.forEach((trigger) => {
        trigger.addEventListener("click", this.handleClick.bind(this));
        trigger.addEventListener("keydown", this.handleKeydown.bind(this));
      });
    }

    handleClick(event) {
      event.preventDefault();

      const index = this.triggers.indexOf(event.currentTarget);

      this.activatePanel(index);
    }

    handleKeydown(event) {
      let flag = false;

      switch (event.key) {
        case "ArrowLeft":
          this.activatePrevious(event.currentTarget);
          flag = true;
          break;

        case "ArrowRight":
          this.activateNext(event.currentTarget);
          flag = true;
          break;

        case "Home":
          this.activatePanel(0);
          flag = true;
          break;

        case "End":
          if (this.panels.length) {
            this.activatePanel(this.panels.length - 1);
          }
          flag = true;
          break;

        default:
          break;
      }

      if (flag) {
        event.stopPropagation();
        event.preventDefault();
      }
    }

    activatePrevious(trigger) {
      const index = this.triggers.indexOf(trigger);

      if (index === 0) {
        this.activatePanel(this.triggers.length - 1);
      } else {
        this.activatePanel(index - 1);
      }
    }

    activateNext(trigger) {
      const index = this.triggers.indexOf(trigger);

      if (index === this.triggers.length - 1) {
        this.activatePanel(0);
      } else {
        this.activatePanel(index + 1);
      }
    }

    activatePanel(index, focus = true) {
      this.deactivateAllPanels();

      this.triggers[index].setAttribute("aria-selected", true);
      this.triggers[index].setAttribute("active", true);
      this.triggers[index].removeAttribute("tabindex");
      this.panels[index].setAttribute("active", true);

      if (focus) {
        this.triggers[index].focus();
      }
    }

    deactivatePanel(index) {
      this.triggers[index].setAttribute("aria-selected", false);
      this.triggers[index].setAttribute("active", false);
      this.triggers[index].tabIndex = -1;
      this.panels[index].setAttribute("active", false);
    }

    deactivateAllPanels() {
      this.triggers.forEach((trigger, index) => this.deactivatePanel(index));
    }

    addAccessibilityAttributes() {
      this.querySelector(this.config.triggersSelector).setAttribute(
        "role",
        "tablist",
      );

      this.triggers.forEach((trigger, index) => {
        trigger.id = `tabs-${this.config.id}-trigger-${index}`;
        trigger.setAttribute("role", "tab");
        trigger.setAttribute("aria-selected", false);
        trigger.setAttribute(
          "aria-controls",
          `tabs-${this.config.id}-panel-${index}`,
        );
      });

      this.panels.forEach((panel, index) => {
        panel.id = `tabs-${this.config.id}-panel-${index}`;
        panel.setAttribute("role", "tabpanel");
        panel.tabIndex = 0;
        panel.setAttribute(
          "aria-labelledby",
          `tabs-${this.config.id}-trigger-${index}`,
        );
      });
    }
  },
);
